import { FDRStatus, FieldFDRStats, ProgramContentListItem } from '@cibo/core'
import { useFDRAcresColumn, useFDRFieldCountColumn, useFDRRewardsColumn } from '@cibo/profile'
import { ProgramSponsorDisplay, useAllProgramContent, useProgramStats } from '@cibo/programs'
import { DataGridPro, ExtendedDataGridProProps } from '@cibo/ui'
import { Button } from '@mui/material'
import { GridColumns, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid-pro'
import { Image } from 'mui-image'
import { ROUTE } from 'navigation/types'
import { pluck, propEq, sum } from 'ramda'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'

export const ProgramsTable = ({ onRowClick }: Pick<ExtendedDataGridProProps, 'onRowClick'>) => {
  const { t } = useTranslation('impact/pages/Admin/Programs/ProgramsTable')
  const programContent = useAllProgramContent()
  const programStats = useProgramStats({})
  const navigate = useNavigate()

  const rows = useMemo(
    () =>
      programContent.data?.map(programContent => {
        const fdrStats = programStats.data?.programs.find(
          propEq('programId', programContent.programId)
        )?.fdrStats

        return {
          ...programContent,
          fdrStats,
        }
      }) || [],
    [programContent.dataUpdatedAt, programStats.dataUpdatedAt]
  )

  const fdrColumns = [
    useFDRFieldCountColumn(FDRStatus.eligibleForEnrollment),
    useFDRAcresColumn(FDRStatus.eligibleForEnrollment),
    useFDRFieldCountColumn(FDRStatus.enrolling),
    useFDRAcresColumn(FDRStatus.enrolling),
    useFDRFieldCountColumn(FDRStatus.enrolled),
    useFDRAcresColumn(FDRStatus.enrolled),
    useFDRRewardsColumn(FDRStatus.enrolled),
  ]

  const columns = useMemo(
    () =>
      [
        {
          field: 'icon',
          headerName: '',
          width: 24,
          renderCell: p => (
            <Image
              duration={200}
              wrapperStyle={{ flexBasis: '24px', alignItems: 'left', height: '24px' }}
              style={{ maxHeight: '24px', maxWidth: '24px', objectFit: 'contain' }}
              src={p.row.content.fields.logo.fields.file.url}
              showLoading
            />
          ),
        },
        {
          field: 'name',
          headerName: t('program'),
          minWidth: 200,
          flex: 1,
          valueGetter: (p: GridValueGetterParams<string>) => p.row.content.fields.title,
        },
        {
          field: 'sponsor',
          headerName: t('Sponsor'),
          sortable: false,
          renderCell: p => <ProgramSponsorDisplay programId={p.id as string} />,
        },
        ...fdrColumns,
        /* Postponed until after MVP
        {
          field: 'dollars',
          headerName: t('dollars'),
          minWidth: 150,
          flex: 1,
          valueGetter: p =>
            sum(
              flatten(
                p.row.opportunities?.map(({ reward }) =>
                  reward.map(r => (r.token === 'usd' ? r.amount : 0))
                ) || []
              )
            ),
          valueFormatter: p => (p.value <= 0 ? '--' : numeral(p.value).format('$0,0')),
        },
        {
          field: 'credits',
          headerName: t('credits'),
          minWidth: 150,
          flex: 1,
          valueGetter: p =>
            sum(
              flatten(
                p.row.opportunities?.map(({ reward }) =>
                  reward.map(r => (r.token === 'carbonCredit' ? r.amount : 0))
                ) || []
              )
            ),
          valueFormatter: p => (p.value <= 0 ? '--' : numeral(p.value).format('0,0')),
        },
        */
        {
          field: 'actions',
          headerName: '',
          width: 110,
          renderCell: (p: GridRenderCellParams) =>
            // @ts-ignore @todo: fix or remove Enroll button
            sum(pluck('numResources', p.row.opportunities || [])) > 0 ? (
              <Button
                variant="outlined"
                size="small"
                onClick={e => {
                  e.stopPropagation()
                  navigate(
                    generatePath([ROUTE.PROGRAMS, ROUTE.ENROLL].join('/'), {
                      programId: p.id as string,
                    })
                  )
                }}
              >
                {t('enroll')}
              </Button>
            ) : null,
        },
      ] as GridColumns<
        ProgramContentListItem & {
          eligible: FieldFDRStats
          enrolling: FieldFDRStats
          enrolled: FieldFDRStats
        }
      >,
    []
  )

  return (
    <DataGridPro
      columns={columns}
      headerPaletteColor="primary"
      getRowId={({ programId }) => programId}
      onRowClick={onRowClick}
      rows={rows}
      loading={programContent.isFetching || programStats.isFetching}
      autoHeight
      hideFooter
    />
  )
}
